import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, resolveUrl } from "@/lib/api";
import { useApi } from "@/lib/useApi";
import { joinStyles } from "@/lib/format";
import { VideoCard } from "@/components/VideoCard";
import { MaterialIcon } from "@/components/MaterialIcon";

const Heading = ({ children }) => <h2 className="text-[19px] md:text-[22px] lg:text-[24px] font-bold leading-tight mb-4 md:mb-5">{children}</h2>;

const ViewAll = ({ to, testId }) => (
  <Link to={to} className="ml-4 lg:ml-6 text-[14px] md:text-[17px] font-bold whitespace-nowrap hover:underline" data-testid={testId}>
    View All
  </Link>
);

const StyleBox = ({ style }) => (
  <Link
    to={`/styles/${style.slug}`}
    className="w-[144px] h-[72px] shrink-0 rounded-xl bg-[#D9D9D9] overflow-hidden flex items-center justify-center text-[13px] font-semibold text-[#333] hover:scale-[1.03] transition-transform duration-200"
    data-testid={`style-box-${style.slug}`}
  >
    {style.image_url ? <img src={resolveUrl(style.image_url)} alt={style.name} className="w-full h-full object-cover" /> : <span className="px-2 text-center">{style.name}</span>}
  </Link>
);

const FestivalCard = ({ festival }) => {
  const to = `/festivals/${festival.id}`;
  const img = festival.cover_image_url || festival.logo_url;
  return (
    <Link to={to} className="w-[144px] shrink-0 group" data-testid={`festival-card-${festival.id}`}>
      <div className="h-[182px] rounded-xl bg-[#D9D9D9] overflow-hidden group-hover:scale-[1.03] transition-transform duration-200">
        {img && <img src={resolveUrl(img)} alt={festival.name} className="w-full h-full object-cover" />}
      </div>
      <p className="mt-3 text-[14px] font-bold leading-tight truncate">{festival.name}</p>
      <p className="text-[11px] font-medium text-[#8A8A8A] truncate">{joinStyles(festival.dance_styles)}</p>
    </Link>
  );
};

const FADE = "linear-gradient(to right, black calc(100% - 56px), transparent)";

const StylesStrip = ({ styles }) => {
  const ref = useRef(null);
  const [overflow, setOverflow] = useState(false);
  const [atEnd, setAtEnd] = useState(false);
  const items = styles.slice(0, 7);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const check = () => setOverflow(el.scrollWidth - el.clientWidth > 4);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, [items.length]);

  const slide = () => {
    const el = ref.current;
    if (!el) return;
    const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 4;
    el.scrollTo({ left: atEnd ? 0 : el.scrollLeft + el.clientWidth - 40, behavior: "smooth" });
  };

  return (
    <div className="flex items-center">
      <div className="relative min-w-0 flex-1">
        <div
          ref={ref}
          onScroll={(e) => setAtEnd(e.target.scrollLeft + e.target.clientWidth >= e.target.scrollWidth - 4)}
          style={overflow && !atEnd ? { maskImage: FADE, WebkitMaskImage: FADE } : undefined}
          className="flex gap-5 overflow-x-auto no-scrollbar snap-x py-1"
          data-testid="search-styles-strip"
        >
          {items.map((s) => (
            <StyleBox key={s.id} style={s} />
          ))}
        </div>
        {overflow && !atEnd && (
          <button
            type="button"
            onClick={slide}
            aria-label="More dance styles"
            className="absolute -right-5 top-1/2 -translate-y-1/2 flex items-center justify-center text-black hover:scale-110 transition-transform duration-200"
            data-testid="search-styles-next-button"
          >
            <span className="absolute w-[36px] h-[36px] rounded-full bg-white" aria-hidden="true" />
            <MaterialIcon name="arrow_circle_right" filled size={48} className="relative" />
          </button>
        )}
      </div>
      <ViewAll to="/styles" testId="search-view-all-styles" />
    </div>
  );
};

const DefaultPanel = ({ styles, festivals }) => (
  <>
    <section data-testid="search-dance-styles">
      <Heading>Dance Styles</Heading>
      <StylesStrip styles={styles} />
      {styles.length === 0 && <p className="text-[14px] text-[#8A8A8A]">No dance styles yet.</p>}
    </section>
    <section className="mt-10" data-testid="search-festivals-this-month">
      <Heading>Festivals This Month</Heading>
      <div className="flex items-start">
        <div className="flex gap-4 md:gap-5 overflow-x-auto no-scrollbar py-1">
          {festivals.slice(0, 7).map((f) => <FestivalCard key={f.id} festival={f} />)}
        </div>
        <div className="h-[182px] flex items-center">
          <ViewAll to="/festivals" testId="search-view-all-festivals" />
        </div>
      </div>
      {festivals.length === 0 && <p className="text-[14px] text-[#8A8A8A]">No festivals scheduled this month.</p>}
    </section>
  </>
);

const Results = ({ results, query }) => {
  const empty = !results.videos.length && !results.festivals.length && !results.dance_styles.length;
  if (empty) return <p className="text-[16px] text-[#8A8A8A]" data-testid="search-no-results">No results found.</p>;
  const shown = results.videos.slice(0, 10);
  const more = (results.video_total ?? results.videos.length) > shown.length;
  return (
    <div className="space-y-10" data-testid="search-results">
      {results.dance_styles.length > 0 && (
        <section>
          <Heading>Dance Styles</Heading>
          <div className="flex gap-4 md:gap-5 flex-wrap">{results.dance_styles.map((s) => <StyleBox key={s.id} style={s} />)}</div>
        </section>
      )}
      {results.festivals.length > 0 && (
        <section>
          <Heading>Festivals</Heading>
          <div className="flex gap-4 md:gap-5 flex-wrap">{results.festivals.map((f) => <FestivalCard key={f.id} festival={f} />)}</div>
        </section>
      )}
      {shown.length > 0 && (
        <section>
          <Heading>Performances</Heading>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">{shown.map((v) => <VideoCard key={v.id} video={v} compact />)}</div>
          {more && (
            <Link
              to={`/search?q=${encodeURIComponent(query.trim())}`}
              className="mt-6 inline-flex items-center gap-1.5 rounded-full bg-[#F8D481] hover:bg-[#f3c968] px-7 py-3 text-[15px] font-bold transition-colors"
              data-testid="search-view-all-results"
            >
              View all results
              <MaterialIcon name="arrow_forward" size={20} weight={600} />
            </Link>
          )}
        </section>
      )}
    </div>
  );
};

export const SearchOverlay = ({ query }) => {
  const { data: styles } = useApi("/dance-styles");
  const { data: festivals } = useApi("/festivals?this_month=true");
  const [results, setResults] = useState(null);

  useEffect(() => {
    const q = query.trim();
    if (!q) {
      setResults(null);
      return;
    }
    let active = true;
    const t = setTimeout(
      () =>
        api
          .get("/search", { params: { q } })
          .then((r) => active && setResults(r.data))
          .catch(() => {}),
      150,
    );
    return () => {
      active = false;
      clearTimeout(t);
    };
  }, [query]);

  return (
    <div className="mx-auto max-w-[1280px] 2xl:max-w-[1560px] 3xl:max-w-[1840px] 4xl:max-w-[2280px] px-4 sm:px-8 lg:px-[100px] pb-28 md:pb-16 lg:pb-14 pt-2 df-panel-in max-h-[calc(100dvh-80px)] lg:max-h-[calc(100dvh-104px)] overflow-y-auto no-scrollbar" data-testid="search-overlay">
      {results ? <Results results={results} query={query} /> : <DefaultPanel styles={styles || []} festivals={festivals || []} />}
    </div>
  );
};
